import { Check, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { TargetOption } from '../schemas/target-kinds';

export interface SearchPickListProps {
  label: string;
  q: string;
  onQChange: (q: string) => void;
  items: readonly TargetOption[] | undefined;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  selectedIds: ReadonlySet<string>;
  onPick: (option: TargetOption) => void;
  /** Hides the search box for bounded lists the backend cannot search. */
  searchable?: boolean;
}

/** A search box over one kind of store record, each result a toggle button. */
export function SearchPickList({
  label,
  q,
  onQChange,
  items,
  isPending,
  error,
  onRetry,
  selectedIds,
  onPick,
  searchable = true,
}: SearchPickListProps) {
  const { t } = useTranslation('linkTargets');

  return (
    <div className="flex flex-col gap-2">
      {searchable ? (
        <div className="relative">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={q}
            onChange={(e) => onQChange(e.target.value)}
            placeholder={t('search')}
            aria-label={t('searchLabel', { label })}
            className="pl-8"
          />
        </div>
      ) : null}
      {error ? (
        <ErrorState error={error} onRetry={onRetry} compact />
      ) : isPending ? (
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9" />
          ))}
        </div>
      ) : !items || items.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
          {t('empty')}
        </p>
      ) : (
        <ul aria-label={label} className="flex max-h-64 flex-col gap-1 overflow-y-auto p-0.5">
          {items.map((option) => {
            const selected = selectedIds.has(option.id);
            return (
              <li key={option.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onPick(option)}
                  className={cn(
                    'hover:bg-accent focus-visible:ring-ring/50 flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm outline-none focus-visible:ring-[3px]',
                    selected && 'border-primary bg-primary-tint/40',
                  )}
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium">{option.label}</span>
                    {option.detail ? (
                      <span className="text-muted-foreground truncate font-mono text-xs">
                        {option.detail}
                      </span>
                    ) : null}
                  </span>
                  {selected ? <Check className="text-primary size-4 shrink-0" aria-hidden /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
